import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_SECURITY, DPA_LABEL, RODO_PROCESSOR_PRESETS, RODO_TEMPLATES, activityFromTemplate, fmtD, renderRodoHtml, rodoCSV, rodoChecks,
  today0, ymd, type Data, type DpaStatus, type RodoActivity, type RodoBundle, type RodoMain, type RodoProcessor, type Settings,
} from '@biuro/shared';
import { api } from '../api.ts';
import { downloadFile, useCopy, useData, useDeleteDoc, useMe, useSaveDoc, useSettings, useToast } from '../hooks.tsx';
import { DeleteButton, Empty, Icon, Sheet } from '../ui.tsx';

const ROLE_LABEL = { controller: 'administrator', processor: 'podmiot przetwarzający' } as const;

function bundle(data: Data): RodoBundle | null {
  const main = data.rodo.main;
  if (!main) return null;
  return { main, activities: Object.entries(data.rodoActivities).sort((a, b) => a[1].order - b[1].order), processors: data.rodoProcessors };
}

/** Wydruk przez ukrytą ramkę – na Macu i iPhonie otwiera systemowe okno drukowania (także „Zapisz jako PDF”). */
function printHtml(html: string) {
  const f = document.createElement('iframe');
  f.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0';
  document.body.appendChild(f);
  const d = f.contentDocument!;
  d.open();
  d.write(html);
  d.close();
  setTimeout(() => {
    f.contentWindow?.focus();
    f.contentWindow?.print();
    setTimeout(() => f.remove(), 60_000);
  }, 250);
}

const bodyOf = (html: string) => html.slice(html.indexOf('<body>') + 6, html.lastIndexOf('</body>'));

export function RodoView() {
  const { data, isLoading } = useData();
  const [wizard, setWizard] = useState(false);
  // Kreator bierze dane firmy przy otwarciu, więc czeka na wczytanie danych (np. przy wejściu prosto na /firma/rodo).
  if (isLoading) return <div className="empty"><span className="spin" aria-label="Ładowanie" /></div>;
  const b = bundle(data);
  if (wizard || !b?.main.wizardDone) return <Wizard onDone={() => setWizard(false)} onCancel={b?.main.wizardDone ? () => setWizard(false) : undefined} />;
  return <Register b={b} onWizard={() => setWizard(true)} />;
}

// ================= Rejestr =================
function Register({ b, onWizard }: { b: RodoBundle; onWizard: () => void }) {
  const me = useMe().data;
  const save = useSaveDoc();
  const toast = useToast();
  const qc = useQueryClient();
  const [preview, setPreview] = useState(false);
  const [editAct, setEditAct] = useState<string | null>(null);
  const [editProc, setEditProc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const html = useMemo(() => renderRodoHtml(b), [b]);
  const checks = rodoChecks(b, today0());
  const ctrl = b.activities.filter(([, a]) => a.role === 'controller');
  const proc = b.activities.filter(([, a]) => a.role === 'processor');
  const day = ymd(today0());

  const review = () => {
    save.mutate({ col: 'rodo', id: 'main', doc: { ...b.main, reviewedAt: day, asOf: day } satisfies RodoMain });
    toast('Zapisano przegląd. Kolejny przypomnę za rok.');
  };
  const toDrive = async () => {
    setBusy(true);
    try {
      await api.rodoDrive();
      await qc.invalidateQueries({ queryKey: ['data'] });
      toast('Zapisano w Dokumentach Google. Link jest w nagłówku rejestru.');
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="panel pad stack">
        <div className="reg-head">
          <div>
            <h2>{b.main.controllerName || 'Administrator'}</h2>
            <div className="muted small">
              Rejestr czynności przetwarzania (art. 30 RODO), stan na {fmtD(b.main.asOf)}{b.main.reviewedAt ? `, ostatni przegląd ${fmtD(b.main.reviewedAt)}` : ''}.
              {b.main.driveDocLink ? <>{' '}<a href={b.main.driveDocLink} target="_blank" rel="noreferrer">Wersja w Dokumentach Google</a></> : null}
            </div>
          </div>
          <div className="actions">
            <button className="btn" onClick={() => setPreview(true)}>Podgląd</button>
            <button className="btn" onClick={() => printHtml(html)}>Drukuj lub PDF</button>
            {me?.google.drive && <button className="btn" disabled={busy} onClick={toDrive}>{busy ? 'Zapisuję…' : 'Do Dokumentów Google'}</button>}
            <button className="btn ghost" onClick={() => downloadFile(`rejestr-czynnosci-${day}.csv`, rodoCSV(b), 'text/csv;charset=utf-8')}>CSV</button>
          </div>
        </div>
        <div className="sync-row">
          <span className="muted small">Rejestr przeglądaj co najmniej raz w roku i przy każdej zmianie narzędzi, dostawców lub celów.</span>
          <div className="actions">
            <button className="btn sm hi" onClick={review}>Przejrzany dziś</button>
            <button className="btn sm ghost" onClick={onWizard}>Kreator od nowa</button>
          </div>
        </div>
      </div>

      {checks.length > 0 && (
        <>
          <h2 className="sec">Do sprawdzenia <small>{checks.length}</small></h2>
          <div className="panel">{checks.map((c) => <div className="al" key={c}><span className="dot" /><div className="al-b">{c}</div></div>)}</div>
        </>
      )}

      <h2 className="sec">Czynności przetwarzania <small>{ctrl.length}</small></h2>
      <div className="panel">
        {ctrl.map(([id, a]) => <ActivityRow key={id} a={a} onClick={() => setEditAct(id)} />)}
        <div className="add-row"><button className="btn sm" onClick={() => setEditAct('')}><Icon.plus />Dodaj czynność</button></div>
      </div>

      {proc.length > 0 && (
        <>
          <h2 className="sec">Przetwarzanie w imieniu klientów <small>(art. 30 ust. 2)</small></h2>
          <div className="panel">{proc.map(([id, a]) => <ActivityRow key={id} a={a} onClick={() => setEditAct(id)} />)}</div>
        </>
      )}

      <h2 className="sec">Podmioty przetwarzające <small>{Object.keys(b.processors).length}</small></h2>
      <div className="panel">
        {Object.entries(b.processors).map(([id, p]) => (
          <div key={id} className="al act plain" role="button" tabIndex={0} onClick={() => setEditProc(id)} onKeyDown={(e) => e.key === 'Enter' && setEditProc(id)}>
            <div className="al-b"><b>{p.name}</b><div>{[p.service, p.country].filter(Boolean).join(', ')}</div></div>
            <span className={`pill ${p.dpa === 'signed' || p.dpa === 'terms' ? 'ok' : p.dpa === 'none' ? 'bad' : 'warn'}`}>{DPA_LABEL[p.dpa]}</span>
          </div>
        ))}
        <div className="add-row"><button className="btn sm" onClick={() => setEditProc('')}><Icon.plus />Dodaj podmiot</button></div>
      </div>

      {preview && (
        <Sheet title="Podgląd rejestru" onClose={() => setPreview(false)}
          footer={<><span /><div className="r"><button className="btn" onClick={() => downloadFile(`rejestr-czynnosci-${day}.html`, html, 'text/html;charset=utf-8')}>Pobierz HTML</button><button className="btn pri" onClick={() => printHtml(html)}>Drukuj / PDF</button></div></>}>
          <div className="rodo-doc" dangerouslySetInnerHTML={{ __html: bodyOf(html) }} />
        </Sheet>
      )}
      {editAct !== null && <ActivityForm id={editAct || undefined} onClose={() => setEditAct(null)} />}
      {editProc !== null && <ProcessorForm id={editProc || undefined} onClose={() => setEditProc(null)} />}
    </>
  );
}

function ActivityRow({ a, onClick }: { a: RodoActivity; onClick: () => void }) {
  return (
    <div className={`al act plain ${a.active ? '' : 'off'}`} role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === 'Enter' && onClick()}>
      <div className="al-b">
        <b>{a.name}</b>
        <div>{a.legalBasis}</div>
        <div>Usunięcie: {a.retention}</div>
      </div>
      <div className="chips">
        {a.transfers && <span className="tag">poza EOG</span>}
        {a.special && <span className="tag">dane szczególne</span>}
        {!a.active && <span className="pill">wyłączona</span>}
      </div>
    </div>
  );
}

// ================= Formularze =================
const FIELDS: [keyof RodoActivity, string, string?][] = [
  ['purposes', 'Cel przetwarzania'],
  ['legalBasis', 'Podstawa prawna', 'Np. art. 6 ust. 1 lit. b RODO (umowa).'],
  ['subjects', 'Kategorie osób'],
  ['dataCategories', 'Kategorie danych'],
  ['source', 'Źródło danych'],
  ['retention', 'Planowany termin usunięcia'],
  ['recipients', 'Kategorie odbiorców', 'Komu dane są ujawniane, w tym odrębnym administratorom (np. przewoźnikom).'],
  ['jointController', 'Współadministrator', 'Puste = nie dotyczy.'],
  ['systems', 'Systemy i aplikacje'],
  ['transfers', 'Przekazanie do państwa trzeciego', 'Puste = dane nie wychodzą poza EOG.'],
  ['transferSafeguards', 'Zabezpieczenia przekazania', 'Np. decyzja KE (EU–US Data Privacy Framework), standardowe klauzule umowne.'],
  ['security', 'Środki bezpieczeństwa tej czynności', 'Puste = opis ogólny z nagłówka rejestru.'],
  ['dpia', 'Ocena skutków (DPIA)'],
];

function ActivityForm({ id, onClose }: { id?: string; onClose: () => void }) {
  const { data } = useData();
  const save = useSaveDoc();
  const del = useDeleteDoc();
  const toast = useToast();
  const cur = id ? data.rodoActivities[id] : undefined;
  const nextOrder = Math.max(0, ...Object.values(data.rodoActivities).map((a) => a.order)) + 1;
  const blank: RodoActivity = {
    name: '', role: 'controller', purposes: '', legalBasis: '', subjects: '', dataCategories: '', special: false, source: '', retention: '', recipients: '',
    processorIds: [], jointController: '', systems: '', security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.', active: true, order: nextOrder,
  };
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<RodoActivity>({ defaultValues: cur ?? blank });
  const role = watch('role');

  const submit = handleSubmit((v) => {
    save.mutate({ col: 'rodoActivities', id: id || 'act-' + Date.now().toString(36), doc: { ...v, processorIds: v.processorIds || [], updatedAt: new Date().toISOString() } });
    toast('Zapisano czynność');
    onClose();
  });

  return (
    <Sheet title={cur?.name || 'Nowa czynność przetwarzania'} onClose={onClose}
      footer={<>
        {id ? <DeleteButton onConfirm={() => { del.mutate({ col: 'rodoActivities', id }); onClose(); }} /> : <span />}
        <div className="r"><button className="btn" onClick={onClose}>Anuluj</button><button className="btn pri" onClick={submit}>Zapisz</button></div>
      </>}>
      <form className="fgrid" onSubmit={submit}>
        {!id && (
          <div className="fld full">
            <label htmlFor="r-tpl">Zacznij od szablonu</label>
            <select id="r-tpl" defaultValue="" onChange={(e) => {
              const t = RODO_TEMPLATES.find((x) => x.key === e.target.value);
              if (t) reset({ ...activityFromTemplate(t, nextOrder), processorIds: t.processors.filter((p) => data.rodoProcessors[p]) });
            }}>
              <option value="">Pusta czynność</option>
              {RODO_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
            </select>
          </div>
        )}
        <div className="fld">
          <label htmlFor="r-name">Nazwa czynności</label>
          <input id="r-name" autoComplete="off" {...register('name', { required: true })} />
          {errors.name && <div className="hint err">Podaj nazwę.</div>}
        </div>
        <div className="fld">
          <label htmlFor="r-role">Rola</label>
          <select id="r-role" {...register('role')}><option value="controller">Administrator</option><option value="processor">Podmiot przetwarzający (w imieniu klienta)</option></select>
        </div>
        {role === 'processor' && <div className="fld full"><label htmlFor="r-onb">Administrator, w imieniu którego przetwarzasz</label><input id="r-onb" {...register('onBehalfOf')} /></div>}
        {FIELDS.map(([k, label, hint]) => (
          <div className="fld full" key={k}>
            <label htmlFor={`r-${k}`}>{label}</label>
            <textarea id={`r-${k}`} rows={2} {...register(k)} />
            {hint && <div className="hint">{hint}</div>}
          </div>
        ))}
        <div className="fld full">
          <div className="lbl">Podmioty przetwarzające</div>
          <div className="checks">
            {Object.entries(data.rodoProcessors).map(([pid, p]) => (
              <label className="chk" key={pid}><input type="checkbox" value={pid} {...register('processorIds')} /> {p.name}</label>
            ))}
            {!Object.keys(data.rodoProcessors).length && <span className="hint">Najpierw dodaj podmioty przetwarzające.</span>}
          </div>
        </div>
        <label className="chk full"><input type="checkbox" {...register('special')} /> Dane szczególnych kategorii (zdrowie, poglądy, biometria) lub o wyrokach</label>
        <label className="chk full"><input type="checkbox" {...register('active')} /> Czynność aktualna (w rejestrze)</label>
      </form>
    </Sheet>
  );
}

function ProcessorForm({ id, onClose }: { id?: string; onClose: () => void }) {
  const { data } = useData();
  const save = useSaveDoc();
  const del = useDeleteDoc();
  const toast = useToast();
  const cur = id ? data.rodoProcessors[id] : undefined;
  const { register, handleSubmit, formState: { errors } } = useForm<RodoProcessor>({
    defaultValues: cur ?? { name: '', service: '', contact: '', country: 'PL', dpa: 'unknown', notes: '' },
  });
  const submit = handleSubmit((v) => {
    save.mutate({ col: 'rodoProcessors', id: id || 'p-' + Date.now().toString(36), doc: v });
    toast('Zapisano podmiot');
    onClose();
  });
  return (
    <Sheet title={cur?.name || 'Nowy podmiot przetwarzający'} onClose={onClose}
      footer={<>
        {id ? <DeleteButton onConfirm={() => { del.mutate({ col: 'rodoProcessors', id }); onClose(); }} /> : <span />}
        <div className="r"><button className="btn" onClick={onClose}>Anuluj</button><button className="btn pri" onClick={submit}>Zapisz</button></div>
      </>}>
      <form className="fgrid" onSubmit={submit}>
        <div className="fld">
          <label htmlFor="p-name">Nazwa</label>
          <input id="p-name" autoComplete="off" {...register('name', { required: true })} />
          {errors.name && <div className="hint err">Podaj nazwę.</div>}
        </div>
        <div className="fld"><label htmlFor="p-country">Kraj / siedziba</label><input id="p-country" {...register('country')} /></div>
        <div className="fld full"><label htmlFor="p-service">Zakres usługi</label><input id="p-service" {...register('service')} /></div>
        <div className="fld full"><label htmlFor="p-contact">Pełna nazwa i kontakt</label><input id="p-contact" placeholder="np. nazwa spółki, adres, e-mail do spraw RODO" {...register('contact')} /></div>
        <div className="fld">
          <label htmlFor="p-dpa">Umowa powierzenia</label>
          <select id="p-dpa" {...register('dpa')}>{(Object.keys(DPA_LABEL) as DpaStatus[]).map((k) => <option key={k} value={k}>{DPA_LABEL[k]}</option>)}</select>
        </div>
        <div className="fld full"><label htmlFor="p-notes">Notatki</label><input id="p-notes" placeholder="np. gdzie jest umowa, data akceptacji" {...register('notes')} /></div>
      </form>
    </Sheet>
  );
}

// ================= Kreator =================
interface Draft {
  main: RodoMain;
  selected: Record<string, boolean>;
  processors: Record<string, RodoProcessor & { include: boolean }>;
  activities: Record<string, RodoActivity>;
}

function initialDraft(data: Data, st: Settings): Draft {
  const existing = data.rodo.main;
  const day = ymd(today0());
  const main: RodoMain = existing ?? {
    controllerName: st.companyName, address: st.address, nip: st.nip, regon: st.regon, email: '', phone: '', representative: '',
    dpo: 'Nie wyznaczono – nie jest wymagany przy tej skali i rodzaju przetwarzania.', security: DEFAULT_SECURITY, processorMode: false, asOf: day,
  };
  const hasActs = Object.keys(data.rodoActivities).length > 0;
  const selected: Record<string, boolean> = {};
  const activities: Record<string, RodoActivity> = {};
  if (hasActs) {
    for (const [id, a] of Object.entries(data.rodoActivities)) {
      activities[id] = a;
      selected[id] = a.active;
    }
  } else {
    RODO_TEMPLATES.forEach((t, i) => { if (t.recommended) { selected[`act-${t.key}`] = true; activities[`act-${t.key}`] = activityFromTemplate(t, i); } });
  }
  const processors: Draft['processors'] = {};
  for (const [id, p] of Object.entries(data.rodoProcessors)) processors[id] = { ...p, include: true };
  for (const [id, p] of Object.entries(RODO_PROCESSOR_PRESETS)) {
    if (processors[id]) continue;
    const name = id === 'p-biuro' && st.accountantName ? `Biuro rachunkowe – ${st.accountantName}` : p.name;
    processors[id] = { ...p, name, include: !hasActs };
  }
  return { main, selected, processors, activities };
}

const STEPS = ['Administrator', 'Czynności', 'Podmioty przetwarzające', 'Szczegóły', 'Podsumowanie'];

function Wizard({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const { data } = useData();
  const st = useSettings();
  const toast = useToast();
  const qc = useQueryClient();
  const copy = useCopy();
  const [step, setStep] = useState(0);
  const [d, setD] = useState<Draft>(() => initialDraft(data, st));
  const [open, setOpen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setMain = (patch: Partial<RodoMain>) => setD((x) => ({ ...x, main: { ...x.main, ...patch } }));
  const included = Object.entries(d.processors).filter(([, p]) => p.include);
  const chosen = Object.entries(d.activities).filter(([id]) => d.selected[id]).sort((a, b) => a[1].order - b[1].order);
  const toggleTpl = (key: string, on: boolean) => {
    const id = Object.keys(d.activities).find((k) => d.activities[k].templateKey === key) || `act-${key}`;
    setD((x) => {
      const t = RODO_TEMPLATES.find((y) => y.key === key)!;
      const acts = x.activities[id] ? x.activities : { ...x.activities, [id]: activityFromTemplate(t, RODO_TEMPLATES.indexOf(t)) };
      return { ...x, activities: acts, selected: { ...x.selected, [id]: on } };
    });
  };
  const setAct = (id: string, patch: Partial<RodoActivity>) => setD((x) => ({ ...x, activities: { ...x.activities, [id]: { ...x.activities[id], ...patch } } }));
  const finalBundle = (): RodoBundle => ({
    main: { ...d.main, wizardDone: true, asOf: ymd(today0()), reviewedAt: ymd(today0()) },
    activities: chosen.map(([id, a]) => [id, { ...a, active: true, processorIds: a.processorIds.filter((p) => d.processors[p]?.include) }] as [string, RodoActivity]),
    processors: Object.fromEntries(included.map(([id, { include: _i, ...p }]) => [id, p])),
  });

  const finish = async () => {
    setSaving(true);
    const b = finalBundle();
    try {
      for (const [id, p] of Object.entries(b.processors)) await api.put('rodoProcessors', id, p);
      for (const id of Object.keys(data.rodoProcessors)) if (!b.processors[id]) await api.del('rodoProcessors', id);
      for (const [id, a] of b.activities) await api.put('rodoActivities', id, { ...a, updatedAt: new Date().toISOString() });
      for (const id of Object.keys(data.rodoActivities)) if (!b.activities.some(([x]) => x === id)) await api.del('rodoActivities', id);
      await api.put('rodo', 'main', b.main);
      await qc.invalidateQueries({ queryKey: ['data'] });
      toast('Zapisano rejestr czynności przetwarzania');
      onDone();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const tplSelected = (key: string) => Object.entries(d.activities).some(([id, a]) => a.templateKey === key && d.selected[id]);
  const customActs = Object.entries(d.activities).filter(([, a]) => !a.templateKey);

  return (
    <div className="panel pad stack wizard">
      <ol className="stepper" aria-label="Kroki kreatora">
        {STEPS.map((s, i) => (
          <li key={s} aria-current={i === step ? 'step' : undefined} className={i < step ? 'done' : ''}>
            <button type="button" onClick={() => setStep(i)}><span>{i + 1}</span>{s}</button>
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="stack">
          <p className="lead muted">Rejestr czynności przetwarzania (art. 30 RODO) prowadzisz jako administrator danych klientów sklepu i kontrahentów. Zwolnienie dla firm do 250 osób nie obejmuje przetwarzania, które odbywa się regularnie, a obsługa zamówień jest regularna.</p>
          <div className="fgrid">
            {([['controllerName', 'Administrator (nazwa firmy)'], ['nip', 'NIP'], ['regon', 'REGON'], ['email', 'E-mail do spraw danych osobowych'], ['phone', 'Telefon'], ['address', 'Adres']] as [keyof RodoMain, string][]).map(([k, l]) => (
              <div className={`fld ${k === 'address' ? 'full' : ''}`} key={k}>
                <label htmlFor={`w-${k}`}>{l}</label>
                <input id={`w-${k}`} value={String(d.main[k] ?? '')} onChange={(e) => setMain({ [k]: e.target.value } as Partial<RodoMain>)} />
              </div>
            ))}
            <div className="fld full">
              <label htmlFor="w-dpo">Inspektor ochrony danych</label>
              <input id="w-dpo" value={d.main.dpo} onChange={(e) => setMain({ dpo: e.target.value })} />
              <div className="hint">IOD jest obowiązkowy m.in. przy monitorowaniu osób na dużą skalę lub przetwarzaniu danych szczególnych na dużą skalę.</div>
            </div>
            <label className="chk full">
              <input type="checkbox" checked={d.main.processorMode} onChange={(e) => {
                setMain({ processorMode: e.target.checked });
                toggleTpl('powierzenie', e.target.checked);
              }} />
              W ramach usług dla klientów mam dostęp do danych osobowych ich klientów lub użytkowników (rejestr kategorii jako podmiot przetwarzający)
            </label>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="stack">
          <p className="muted">Zaznaczyłem czynności, które wynikają z prowadzenia sklepu, fakturowania i współpracy B2B. Odznacz te, których nie robisz, i zaznacz brakujące.</p>
          <div className="tpls">
            {RODO_TEMPLATES.filter((t) => t.role !== 'processor' || d.main.processorMode).map((t) => (
              <label key={t.key} className={`tpl ${tplSelected(t.key) ? 'on' : ''}`}>
                <input type="checkbox" checked={tplSelected(t.key)} onChange={(e) => toggleTpl(t.key, e.target.checked)} />
                <div><b>{t.name}</b><span>{t.hint}</span></div>
              </label>
            ))}
            {customActs.map(([id, a]) => (
              <label key={id} className={`tpl ${d.selected[id] ? 'on' : ''}`}>
                <input type="checkbox" checked={!!d.selected[id]} onChange={(e) => setD((x) => ({ ...x, selected: { ...x.selected, [id]: e.target.checked } }))} />
                <div><b>{a.name}</b><span>Własna czynność</span></div>
              </label>
            ))}
          </div>
          <AddCustom onAdd={(name) => {
            const id = 'act-' + Date.now().toString(36);
            const a: RodoActivity = { ...activityFromTemplate({ ...RODO_TEMPLATES[0], key: '' }, 100), name, templateKey: undefined, purposes: '', legalBasis: '', subjects: '', dataCategories: '', source: '', retention: '', recipients: '', processorIds: [], systems: '', jointController: '' };
            setD((x) => ({ ...x, activities: { ...x.activities, [id]: a }, selected: { ...x.selected, [id]: true } }));
          }} />
        </div>
      )}

      {step === 2 && (
        <div className="stack">
          <p className="muted">Podmioty przetwarzające przetwarzają dane w Twoim imieniu i potrzebują umowy powierzenia (art. 28 RODO). U dostawców SaaS jest ona zwykle częścią regulaminu albo do akceptacji w panelu. Przewoźnicy i platformy społecznościowe to najczęściej odrębni administratorzy – wpisujesz ich jako odbiorców w czynnościach.</p>
          <div className="stack">
            {Object.entries(d.processors).map(([id, p]) => (
              <div key={id} className={`procrow ${p.include ? '' : 'off'}`}>
                <label className="chk"><input type="checkbox" checked={p.include} onChange={(e) => setD((x) => ({ ...x, processors: { ...x.processors, [id]: { ...p, include: e.target.checked } } }))} /></label>
                <input aria-label="Nazwa" value={p.name} onChange={(e) => setD((x) => ({ ...x, processors: { ...x.processors, [id]: { ...p, name: e.target.value } } }))} />
                <input aria-label="Zakres usługi" value={p.service} onChange={(e) => setD((x) => ({ ...x, processors: { ...x.processors, [id]: { ...p, service: e.target.value } } }))} />
                <select aria-label="Umowa powierzenia" value={p.dpa} onChange={(e) => setD((x) => ({ ...x, processors: { ...x.processors, [id]: { ...p, dpa: e.target.value as DpaStatus } } }))}>
                  {(Object.keys(DPA_LABEL) as DpaStatus[]).map((k) => <option key={k} value={k}>{DPA_LABEL[k]}</option>)}
                </select>
              </div>
            ))}
          </div>
          <AddCustom label="Dodaj podmiot" placeholder="Nazwa dostawcy" onAdd={(name) => {
            const id = 'p-' + Date.now().toString(36);
            setD((x) => ({ ...x, processors: { ...x.processors, [id]: { name, service: '', country: 'PL', dpa: 'unknown', include: true } } }));
          }} />
        </div>
      )}

      {step === 3 && (
        <div className="stack">
          <div className="fld">
            <label htmlFor="w-sec">Ogólny opis środków bezpieczeństwa</label>
            <textarea id="w-sec" rows={4} value={d.main.security} onChange={(e) => setMain({ security: e.target.value })} />
            <div className="hint">To propozycja – zostaw tylko to, co faktycznie stosujesz.</div>
          </div>
          {chosen.map(([id, a]) => (
            <div key={id} className="acc">
              <button type="button" className="acc-head" aria-expanded={open === id} onClick={() => setOpen(open === id ? null : id)}>
                <span><b>{a.name}</b><span className="muted small" style={{ display: 'block' }}>{a.role === 'processor' ? ROLE_LABEL.processor : a.legalBasis.slice(0, 80)}</span></span>
                <span aria-hidden>{open === id ? '−' : '+'}</span>
              </button>
              {open === id && (
                <div className="fgrid acc-body">
                  <div className="fld full"><label htmlFor={`w-${id}-name`}>Nazwa</label><input id={`w-${id}-name`} value={a.name} onChange={(e) => setAct(id, { name: e.target.value })} /></div>
                  {a.role === 'processor' && <div className="fld full"><label htmlFor={`w-${id}-onb`}>Administrator, w imieniu którego przetwarzasz</label><input id={`w-${id}-onb`} value={a.onBehalfOf || ''} onChange={(e) => setAct(id, { onBehalfOf: e.target.value })} /></div>}
                  {FIELDS.map(([k, label]) => (
                    <div className="fld full" key={k}>
                      <label htmlFor={`w-${id}-${k}`}>{label}</label>
                      <textarea id={`w-${id}-${k}`} rows={2} value={String(a[k] ?? '')} onChange={(e) => setAct(id, { [k]: e.target.value } as Partial<RodoActivity>)} />
                    </div>
                  ))}
                  <div className="fld full">
                    <div className="lbl">Podmioty przetwarzające</div>
                    <div className="checks">
                      {included.map(([pid, p]) => (
                        <label className="chk" key={pid}>
                          <input type="checkbox" checked={a.processorIds.includes(pid)} onChange={(e) => setAct(id, { processorIds: e.target.checked ? [...a.processorIds, pid] : a.processorIds.filter((x) => x !== pid) })} /> {p.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {!chosen.length && <Empty title="Brak czynności">Wróć do kroku 2 i zaznacz czynności.</Empty>}
        </div>
      )}

      {step === 4 && (() => {
        const b = finalBundle();
        const html = renderRodoHtml(b);
        const checks = rodoChecks(b, today0());
        return (
          <div className="stack">
            <p className="muted">Rejestr: {b.activities.length} czynności, {Object.keys(b.processors).length} podmiotów przetwarzających. Po zapisaniu wydrukujesz go do PDF albo zapiszesz w Dokumentach Google.</p>
            {checks.length > 0 && <div className="note warn">{checks.map((c) => <div key={c}>{c}</div>)}</div>}
            <div className="rodo-doc framed" dangerouslySetInnerHTML={{ __html: bodyOf(html) }} />
            <div className="actions"><button className="btn sm ghost" onClick={() => copy(rodoCSV(b))}>Kopiuj jako CSV</button></div>
          </div>
        );
      })()}

      <div className="sheet-foot">
        {onCancel ? <button className="btn ghost" onClick={onCancel}>Anuluj</button> : <span />}
        <div className="r">
          {step > 0 && <button className="btn" onClick={() => setStep(step - 1)}>Wstecz</button>}
          {step < STEPS.length - 1
            ? <button className="btn pri" onClick={() => setStep(step + 1)}>Dalej</button>
            : <button className="btn hi" disabled={saving || !chosen.length} onClick={finish}>{saving ? 'Zapisuję…' : 'Zapisz rejestr'}</button>}
        </div>
      </div>
    </div>
  );
}

function AddCustom({ onAdd, label = 'Dodaj własną czynność', placeholder = 'Nazwa czynności' }: { onAdd: (name: string) => void; label?: string; placeholder?: string }) {
  const [v, setV] = useState('');
  return (
    <div className="copyrow">
      <input value={v} placeholder={placeholder} aria-label={placeholder} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && v.trim()) { e.preventDefault(); onAdd(v.trim()); setV(''); } }} />
      <button type="button" className="btn sm" disabled={!v.trim()} onClick={() => { onAdd(v.trim()); setV(''); }}><Icon.plus />{label}</button>
    </div>
  );
}
